import {
  type QueryObserverResult,
  type RefetchOptions,
} from "@tanstack/react-query";
import React from "react";
import { type PillarApiError } from "shared/api/PillarApiError";
import {
  type CustomReportResults,
  type DynamicFilterChoices,
  type PaginatedResults,
  type PaginationRequest,
} from "shared/api/types/pagination";
import { type CustomReportResponseType } from "shared/api/types/society/[societyId]/reports/[reportId]";
import { type RenderCustomReportColumnFn } from "shared/components/pillar-table/custom-reports/customReportColumnsMapper";
import { type UsePillarTableVirtualValuesResult } from "shared/components/pillar-table/hooks/usePillarTableVirtualValues";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { type FiltersRequest } from "shared/filter-where-clause";

export enum PaginationType {
  Regular = "regular",
  Infinity = "infinity",
}

export type DataQueryFunction<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
> = (
  filters: FiltersRequest,
  paginationRequest?: PaginationRequest,
) => Promise<PaginatedResults<RowGeneric, ExtraPropsGeneric>>;
export type PillarTableContextType<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  testidTable: string;
  queryKey?: string | string[];
  emptyTableMessage?: string;
  loadedFirstTime: boolean;
  wrapperClassName?: string;
  customReportResponse?: CustomReportResults<CustomReportResponseType>;
  tableRowResults: RowGeneric[];
  dataQuery?: DataQueryFunction<RowGeneric, ExtraPropsGeneric>;
  /**
   * What `dataQuery` failed with, when it failed. Rows already on screen are
   * left alone, so this reads as "the last load failed", not "there is no data".
   */
  dataQueryError?: PillarApiError;
  dynamicFilterChoices: DynamicFilterChoices;
  totalResults: number;
  extraProps?: ExtraPropsGeneric;
  columns: React.ReactElement[];
  toggleSorting: (orderBy: string) => void;
  fixedColumnWidths: number[];
  columnRefs: React.RefObject<(HTMLDivElement | null)[]>;
  urlParams?: boolean;
  setFilters: (incomingFilters: FiltersRequest) => void;
  filtersRequest: FiltersRequest;
  pagination: boolean | PaginationType;
  tablePaginationRef?: React.RefObject<HTMLDivElement>;
  isDynamicHeight?: boolean;
  paginationState: PaginationRequest | undefined;
  refetchData?: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<PaginatedResults<RowGeneric, ExtraPropsGeneric>>
  >;
  HeaderBarComponentWrapper: () => React.ReactElement;
  FilterComponentWrapper: () => React.ReactElement;
  className: string | undefined;
  setPaginationState: React.Dispatch<
    React.SetStateAction<PaginationRequest | undefined>
  >;
  isFetchingPreviousPage?: boolean;
  isFetchingNextPage?: boolean;
  renderCustomReportColumn: RenderCustomReportColumnFn;
} & React.ComponentPropsWithoutRef<"div"> &
  PillarTableRowFunctions<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  > &
  UsePillarTableVirtualValuesResult<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >;

export const PillarTableContext = React.createContext<PillarTableContextType<
  PillarTableSafeAny,
  PillarTableSafeAny,
  PillarTableSafeAny
> | null>(null);

export const usePillarTableContext = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>() => {
  const context = React.useContext(PillarTableContext);
  if (!context) {
    throw new Error(
      "PillarTableContext must be used within a PillarTableContextProvider",
    );
  }
  return context as PillarTableContextType<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >;
};

export type UseQueryRefetch<ResponseGeneric> = (
  options?: RefetchOptions,
) => Promise<QueryObserverResult<ResponseGeneric>>;
export type PillarTableContextFactoryProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  testid?: string;
  children: React.ReactNode;
  emptyTableMessage?: string;
  queryKey: string | string[];
  pagination?: boolean | PaginationType;
  isDynamicHeight?: boolean;
  dataQuery: DataQueryFunction<RowGeneric, ExtraPropsGeneric>;
  centered?: boolean;
  exportToExcel?: boolean;
  urlParams?: boolean;
  renderCustomReportColumn?: RenderCustomReportColumnFn;
  isTableWithFixedHeight?: boolean;
  pillarTableContext?: Partial<
    PillarTableContextType<RowGeneric, ExtraPropsGeneric, InlineEditFormGeneric>
  >;
} & React.ComponentPropsWithoutRef<"div"> &
  PillarTableRowFunctions<RowGeneric, ExtraPropsGeneric, InlineEditFormGeneric>;

type PillarTableRowFunctions<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  handleRowClick?: (row: RowGeneric) => void;
  handleInlineEdit?: (
    row: RowGeneric,
    formValues: InlineEditFormGeneric,
  ) => Promise<RowGeneric | void | boolean>;
  handleInlineDelete?: (
    row: RowGeneric,
    refetchFn?: UseQueryRefetch<
      PaginatedResults<RowGeneric, ExtraPropsGeneric>
    >,
  ) => Promise<void | boolean>;
};
